import type { PlatformAuthCopy } from './types';

type AuthCopyOverrides = Partial<Omit<PlatformAuthCopy, 'waiting' | 'embed'>> & {
  waiting?: Partial<PlatformAuthCopy['waiting']>;
  embed?: Partial<PlatformAuthCopy['embed']>;
};

/**
 * Builds a platform's authorize copy from a generic template, so a platform with
 * nothing special to say still reads like a finished screen rather than a bare
 * button. Pass overrides for the parts worth writing by hand.
 */
export const makeAuthCopy = (
  title: string,
  overrides: AuthCopyOverrides = {}
): PlatformAuthCopy => {
  const { waiting, embed, ...rest } = overrides;
  // The inline variant is the same promise in a smaller box, so it defaults to the
  // body rather than inventing a second generic sentence to keep in sync.
  const body = `Allow access to your ${title} account so StreamAlive can connect to your sessions automatically.`;
  return {
    inlineBody: body,
    body,
    helper: `You'll approve access on a ${title}’s site in a new tab; approve it there, then come back here. You can disconnect anytime.`,
    buttonLabel: 'Authorize',
    successToast: `${title} connected successfully`,
    disconnectToast: `${title} disconnected successfully`,
    ...rest,
    waiting: {
      title: 'Waiting for you to approve in the other tab…',
      body: `Approve StreamAlive on ${title}’s site, then come back here. This form updates on its own once you’re done.`,
      check: 'Check your browser.',
      retryPrefix: `If the ${title} authorization page didn’t open,`,
      retryLabel: 'please try again',
      ...waiting,
    },
    embed: {
      title: `Allow access to your ${title} account`,
      body: (embedPlatform: string) =>
        `${title} can’t be authorized from inside ${embedPlatform}. Copy the link below, open it in your browser and authorize StreamAlive — this form updates on its own once you’re done.`,
      helper: `Opens ${title}’s site in your browser; approve it there, then come back here.`,
      copyLabel: 'Copy link',
      copiedLabel: 'Copied',
      ...embed,
    },
  };
};
